import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { logToFile } from '../utils/logging';
import { isHostedWeb } from '../config/cloud';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defense: a render error shows a recovery screen (reload +
 * export-backup) instead of a white screen, and lands in the crash log.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logToFile('error', `Render crash: ${error.message}\n${error.stack ?? ''}\n${info.componentStack ?? ''}`);
  }

  private exportBackup = async () => {
    try {
      // Lazy imports: if the crash happened before the DB module loaded, this
      // still shouldn't take the recovery screen down with it.
      const { exportDatabaseBytes } = await import('../db/database');
      const { downloadBytes } = await import('../utils/download');
      const bytes = await exportDatabaseBytes();
      downloadBytes(`dental-coach-backup-${new Date().toISOString().slice(0, 10)}.sqlite`, bytes);
    } catch (e) {
      alert(`Backup export failed: ${(e as Error).message}`);
    }
  };

  render() {
    if (!this.state.error) return this.props.children;
    // Hosted-web customers' data lives in their cloud account; the raw sqlite
    // backup is a desktop/internal recovery tool.
    const hostedWeb = isHostedWeb();
    return (
      <Box sx={{ p: 6, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Something went wrong in the app
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {this.state.error.message}
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hostedWeb
            ? 'Your prep is saved to your account and is safe. Reload to keep going.'
            : 'Your data is stored locally and is safe. You can export a backup right now, then reload the app.'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload App
          </Button>
          {!hostedWeb && (
            <Button variant="outlined" onClick={this.exportBackup}>
              Export Backup First
            </Button>
          )}
        </Stack>
      </Box>
    );
  }
}
